import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { throwError, Observable, of } from 'rxjs';
import { catchError, expand, map, reduce, takeWhile } from 'rxjs/operators';

import { Signature } from '@digital-platform/docusign/domain';
import {
  FindManifestSearchApiResponse,
  ValidateManifestProfileNumbersApiResponse,
  IManifestType,
} from '@digital-platform/manifests/domain';

@Injectable({
  providedIn: 'root',
})
export class ManifestsService {
  constructor(private httpClient: HttpClient) {}

  public create(newManifest: IManifestType): Observable<IManifestType> {
    const url = '/api/manifests';
    return this.httpClient.post<IManifestType>(url, newManifest).pipe(
      map((response) => response),
      catchError(this.handleError)
    );
  }

  public find(manifestId: string): Observable<IManifestType> {
    const url = `/api/manifests/${manifestId}`;
    return this.httpClient
      .get<IManifestType>(url)
      .pipe(catchError(this.handleError));
  }

  public delete(manifestId: string): Observable<IManifestType> {
    const url = `/api/manifests/${manifestId}`;
    return this.httpClient
      .delete<IManifestType>(url)
      .pipe(catchError(this.handleError));
  }

  public update(
    manifestId: string,
    manifest: IManifestType
  ): Observable<IManifestType> {
    const url = `/api/manifests/${manifestId}`;
    return this.httpClient.put<IManifestType>(url, manifest).pipe(
      map((response) => {
        if (response?.status === '400') {
          throw new Error(response.message);
        }
        return response;
      }),
      catchError(this.handleError)
    );
  }

  public rejectManifest(
    id: string,
    discrepancyNotes: string
  ): Observable<IManifestType> {
    const discrepancyNotesObject = { discrepancyNotes: discrepancyNotes };
    const url = `/api/manifests/${id}/reject`;
    return this.httpClient.put<IManifestType>(url, discrepancyNotesObject).pipe(
      map((response) => response),
      catchError(this.handleError)
    );
  }

  public resendEmail(id: string, signature: Signature): Observable<Signature> {
    //const differentSigner= { signature: signature };
    const url = `/api/manifests/${id}/resend`;
    return this.httpClient.put<Signature>(url, signature).pipe(
      map((response) => response),
      catchError(this.handleError)
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(error);
    }

    let message = 'Something bad happened; please try again later.';

    if (error?.message) {
      message = error.message;
    }

    if (error?.error?.message) {
      message = error.error.message;
    }

    // Return an observable with a user-facing error message.
    return throwError(message);
  }

  public search(
    searchby: {
      key: string;
      value: string;
    }[],
    ExclusiveStartKey?: string
  ): Observable<FindManifestSearchApiResponse> {
    const url = 'api/manifests/search';
    const params = { pageSize: '500' };
    const headers = ExclusiveStartKey
      ? {
          'X-RSC-Page': ExclusiveStartKey,
        }
      : {};
    for (const iterator of searchby) {
      params[iterator.key] = iterator.value;
    }
    return this.getResponse(url, params, headers).pipe(
      map((response: HttpResponse<any>): FindManifestSearchApiResponse => {
        const LastEvaluatedKey = response?.headers?.get('X-RSC-Page');
        return {
          LastEvaluatedKey,
          Items: response?.body,
        };
      }),
      catchError(this.handleError)
    );
  }

  public validateAccessCode(accessCode: string): Observable<string> {
    const url = `/api/manifests/get-signing-link`;

    const params = { accessCode };
    return this.httpClient
      .get<string>(url, {
        observe: 'response',
        params,
      })
      .pipe(
        map((response) => {
          const signingLink = response.body;
          return signingLink;
        }),
        catchError(this.handleError)
      );
  }

  public validateProfileNumbers(
    profileNumbers: string[]
  ): Observable<ValidateManifestProfileNumbersApiResponse[]> {
    const url = `/api/manifests/validate`;
    return this.httpClient
      .post<ValidateManifestProfileNumbersApiResponse[]>(url, {
        profileNumbers,
      })
      .pipe(catchError(this.handleError));
  }

  public findAll(customerName?: string): Observable<IManifestType[]> {
    const url = '/api/manifests';
    const params = { pageSize: '500' };
    if (customerName) {
      params['customerName'] = customerName;
    }

    const headers = new HttpHeaders();

    return this.getManifestResponse(url, params, headers).pipe(
      expand((response: FindManifestSearchApiResponse) => {
        const lastEvaluatedKey = response.LastEvaluatedKey;
        if (lastEvaluatedKey) {
          const newHeaders = new HttpHeaders({
            'X-RSC-Page': lastEvaluatedKey,
          });
          return this.getManifestResponse(url, params, newHeaders);
        } else {
          return of(null);
        }
      }),
      takeWhile((response) => response !== null),
      reduce(
        (acc, response) => {
          acc.Items = acc.Items.concat(response.Items);
          return acc;
        },
        { Items: [] } as FindManifestSearchApiResponse
      ),
      map((accumulatedResponse) => accumulatedResponse.Items),
      catchError(this.handleError)
    );
  }

  private getResponse(
    url: string,
    params?:
      | HttpParams
      | {
          [param: string]: string | string[];
        },
    headers?: {
      [param: string]: string | string[];
    }
  ): Observable<HttpResponse<IManifestType[]>> {
    return this.httpClient.get<IManifestType[]>(url, {
      observe: 'response',
      params,
      headers,
    });
  }

  private getManifestResponse(
    url: string,
    params: any,
    headers: HttpHeaders
  ): Observable<FindManifestSearchApiResponse> {
    return this.httpClient
      .get<IManifestType[]>(url, { params, headers, observe: 'response' })
      .pipe(
        map(
          (
            response: HttpResponse<IManifestType[]>
          ): FindManifestSearchApiResponse => {
            const LastEvaluatedKey = response.headers.get('X-RSC-Page');
            return {
              LastEvaluatedKey,
              Items: response.body || [],
            };
          }
        )
      );
  }
}
